import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Check, Loader2, Star, Zap, Crown, AlertCircle, Wifi, Settings, ExternalLink } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { stripeProducts } from '../stripe-config';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../lib/i18n';

const Pricing = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const handlePurchase = async (priceId: string, mode: 'payment' | 'subscription') => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(priceId);
    setError(null);
    setDebugInfo(null);

    try {
      // Check network connectivity first
      if (!navigator.onLine) {
        throw new Error(t('pricing.errOffline'));
      }

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error(t('pricing.errSessionExpired'));
      }

      // Check if Supabase is properly configured
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!baseUrl) {
        throw new Error(t('pricing.paymentConfigError'));
      }

      console.log('Creating checkout session for:', { priceId, mode });

      const functionsUrl = `${baseUrl}/functions/v1/stripe-checkout`;
      
      const requestBody = {
        price_id: priceId,
        mode,
        success_url: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/pricing`,
      };

      console.log('Request URL:', functionsUrl);
      console.log('Request body:', requestBody);
      console.log('🕐 Payment attempt timestamp:', new Date().toISOString());

      // Set debug info for troubleshooting
      setDebugInfo({
        functionsUrl,
        requestBody,
        hasSession: !!session,
        userEmail: user.email,
        timestamp: new Date().toISOString(),
        environment: {
          supabaseUrl: baseUrl,
          isDev: import.meta.env.DEV,
          mode: import.meta.env.MODE
        }
      });

      // First, let's test if the function exists by making a simple health check
      try {
        const healthResponse = await fetch(`${baseUrl}/functions/v1/stripe-checkout`, {
          method: 'OPTIONS',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        });
        
        console.log('Health check response:', healthResponse.status);
        
        if (healthResponse.status === 404) {
          throw new Error('FUNCTION_NOT_DEPLOYED');
        }
      } catch (healthError) {
        console.error('Health check failed:', healthError);
        if (healthError.message === 'FUNCTION_NOT_DEPLOYED') {
          throw new Error(t('pricing.errNotDeployed'));
        }
      }

      const response = await fetch(functionsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        let errorMessage = t('pricing.serverError', { status: response.status });
        let errorCode = 'UNKNOWN_ERROR';
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
          errorCode = errorData.code || errorCode;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        // Provide more specific error messages based on status codes and error codes
        if (response.status === 404) {
          errorMessage = t('pricing.errNotFound');
        } else if (response.status === 401) {
          errorMessage = t('pricing.errAuthFailed');
        } else if (response.status === 500) {
          if (errorCode === 'STRIPE_NOT_CONFIGURED') {
            errorMessage = t('pricing.errNotConfigured');
          } else {
            errorMessage = t('pricing.errUnavailable');
          }
        } else if (response.status === 400) {
          if (errorCode === 'MISSING_PARAMETERS') {
            errorMessage = t('pricing.errInvalidParams');
          } else if (errorCode === 'INVALID_PRICE') {
            errorMessage = t('pricing.errInvalidPrice');
          }
        }
        
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('Success response:', responseData);
      console.log('🎉 Checkout session created successfully at:', new Date().toISOString());

      // Check if this was an upgrade (no checkout URL, immediate subscription update)
      if (responseData.upgraded) {
        console.log('✅ Subscription upgraded successfully with proration');
        console.log('💰 Prorated amount will be charged on next billing cycle');

        // Show success message and redirect to success page
        navigate('/success?upgraded=true');
        return;
      }

      const { url, sessionId } = responseData;

      // Check if we got a proper Stripe URL
      console.log('🔗 Checkout URL analysis:', {
        url,
        isStripeUrl: url?.includes('checkout.stripe.com'),
        urlLength: url?.length || 0,
        sessionId
      });

      if (!url?.includes('checkout.stripe.com')) {
        console.error('❌ Invalid checkout URL received:', url);
        throw new Error(t('pricing.errInvalidUrl'));
      }

      if (url) {
        console.log('Redirecting to Stripe checkout:', url);
        console.log('🔄 Redirect timestamp:', new Date().toISOString());
        console.log('💡 After payment, check browser console and Supabase logs for subscription processing');
        // Redirect to Stripe Checkout
        window.location.href = url;
      } else {
        throw new Error(t('pricing.errNoUrl'));
      }
    } catch (err: any) {
      console.error('Checkout error:', err);
      console.error('🕐 Error timestamp:', new Date().toISOString());
      
      // Provide user-friendly error messages
      let userMessage = err.message || t('pricing.errFailedCheckout');
      
      if (err.message?.includes('fetch') || err.message?.includes('network')) {
        userMessage = t('pricing.errNetwork');
      } else if (err.message?.includes('session') || err.message?.includes('Authentication') || err.message?.includes('Phiên')) {
        userMessage = t('pricing.errSessionExpired');
        // Optionally redirect to auth page after a delay
        setTimeout(() => navigate('/auth'), 3000);
      } else if (err.message?.includes('configuration') || err.message?.includes('not found') || err.message?.includes('not deployed')) {
        userMessage = t('pricing.errNotProperlyConfigured');
      } else if (err.message?.includes('offline') || err.message?.includes('ngoại tuyến')) {
        userMessage = t('pricing.errOffline');
      }
      
      setError(userMessage);
      
      // Add refresh suggestion for subscription issues
      if (err.message?.includes('subscription') || err.message?.includes('plan')) {
        setTimeout(() => {
          if (window.confirm(t('pricing.errSubscribeConfirm'))) {
            window.location.reload();
          }
        }, 2000);
      }
    } finally {
      setLoading(null);
    }
  };

  const getProductIcon = (productName: string) => {
    if (productName.includes('Team')) {
      return <Crown className="w-8 h-8 text-purple-600" />;
    }
    if (productName.includes('Basic')) {
      return <Star className="w-8 h-8 text-blue-600" />;
    }
    return <Zap className="w-8 h-8 text-green-600" />;
  };

  const getProductFeatures = (productName: string) => {
    if (productName.includes('Team')) {
      return [
        t('pricing.feat.teamPremiumAI'),
        t('pricing.feat.unlimitedDocs'),
        t('pricing.feat.unlimitedSize'),
        t('pricing.feat.realtimeCollab'),
        t('pricing.feat.commentSystem'),
        t('pricing.feat.versionHistory'),
        t('pricing.feat.allExports'),
        t('pricing.feat.storage100'),
        t('pricing.feat.backups'),
        t('pricing.feat.apiAccess'),
        t('pricing.feat.prioritySupport')
      ];
    }

    if (productName.includes('Basic')) {
      return [
        t('pricing.feat.basicAI'),
        t('pricing.feat.upTo50Docs'),
        t('pricing.feat.standardSize'),
        t('pricing.feat.teamCollab'),
        t('pricing.feat.commentSystem'),
        t('pricing.feat.versionHistory'),
        t('pricing.feat.multipleExports'),
        t('pricing.feat.storage5'),
        t('pricing.feat.backups'),
        t('pricing.feat.emailSupport')
      ];
    }

    return [
      t('pricing.feat.advancedAI'),
      t('pricing.feat.unlimitedDocs'),
      t('pricing.feat.largeSize'),
      t('pricing.feat.realtimeCollab'),
      t('pricing.feat.commentSystem'),
      t('pricing.feat.versionHistory'),
      t('pricing.feat.enhancedExports'),
      t('pricing.feat.storage25'),
      t('pricing.feat.backups'),
      t('pricing.feat.prioritySupport')
    ];
  };

  const getPopularBadge = (productName: string) => {
    if (productName.includes('AI') && !productName.includes('Basic')) {
      return (
        <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-center py-2 text-sm font-bold">
          {t('pricing.mostPopular')}
        </div>
      );
    }
    return null;
  };

  if (!user) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-6 p-8 max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <Star className="w-16 h-16 text-blue-500 mx-auto" />
          <h2 className="text-2xl font-bold">{t('pricing.pleaseSignIn')}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t('pricing.pleaseSignInMessage')}
          </p>
          <Button onClick={() => navigate('/auth')} className="w-full">
            {t('pricing.signInToContinue')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          {t('pricing.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-6 py-4 rounded-lg animate-fadeIn max-w-4xl mx-auto">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-bold text-lg mb-1">{t('pricing.paymentError')}</h4>
              <p className="mb-3">{error}</p>
              
              {/* Setup Guide Link */}
              {(error.includes('not configured') || error.includes('not deployed') || error.includes('not found')) && (
                <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded border border-red-200 dark:border-red-700 mb-3">
                  <h5 className="font-medium mb-2">{t('pricing.setupRequired')}</h5>
                  <p className="text-sm mb-3">
                    {t('pricing.setupDescription')}
                  </p>
                  <ul className="text-sm space-y-1 mb-3">
                    <li>• {t('pricing.setupStripeKeys')}</li>
                    <li>• {t('pricing.setupEdgeFunctions')}</li>
                    <li>• {t('pricing.setupWebhooks')}</li>
                  </ul>
                  <Button
                    onClick={() => navigate('/payment-setup')}
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-100"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('pricing.viewSetupGuide')}
                  </Button>
                </div>
              )}
              
              {/* Troubleshooting steps */}
              <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded border border-red-200 dark:border-red-700">
                <h5 className="font-medium mb-2">{t('pricing.troubleshootingSteps')}</h5>
                <ul className="text-sm space-y-1">
                  <li>• {t('pricing.troubleInternet')}</li>
                  <li>• {t('pricing.troubleRefresh')}</li>
                  <li>• {t('pricing.troubleCache')}</li>
                  <li>• {t('pricing.troubleAdBlock')}</li>
                  <li>• {t('pricing.troubleBrowser')}</li>
                </ul>
              </div>

              {/* Debug information for developers */}
              {debugInfo && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium">{t('pricing.debugInfo')}</summary>
                  <pre className="mt-2 p-2 bg-red-50 dark:bg-red-900/30 rounded text-xs overflow-auto">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </details>
              )}
            </div>
            <button
              onClick={() => {
                setError(null);
                setDebugInfo(null);
              }}
              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200 p-1 rounded hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Connection Status Indicator */}
      <div className="flex items-center justify-center space-x-4 text-sm">
        <div className="flex items-center space-x-2">
          <Wifi className={`w-4 h-4 ${navigator.onLine ? 'text-green-500' : 'text-red-500'}`} />
          <span className={navigator.onLine ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
            {navigator.onLine ? t('pricing.connected') : t('pricing.offline')}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Settings className="w-4 h-4 text-blue-500" />
          <span className="text-gray-600 dark:text-gray-400">
            Supabase: {import.meta.env.VITE_SUPABASE_URL ? t('pricing.supabaseConfigured') : t('pricing.supabaseNotConfigured')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {stripeProducts.map((product, index) => (
          <div
            key={product.id}
            className={`relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl hover:scale-105 ${
              product.name.includes('AI') && !product.name.includes('Basic') ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
            }`}
          >
            {getPopularBadge(product.name)}

            <div className={`p-8 ${product.name.includes('AI') && !product.name.includes('Basic') ? 'pt-12' : ''}`}>
              <div className="flex items-center justify-center mb-6">
                {getProductIcon(product.name)}
              </div>

              <h3 className="text-2xl font-bold text-center mb-2">{product.name}</h3>
              
              <div className="text-center mb-6">
                <span className="text-4xl font-bold">${product.price}</span>
                <span className="text-gray-500 dark:text-gray-400 ml-2">
                  {product.mode === 'subscription' ? t('pricing.perMonth') : t('pricing.oneTime')}
                </span>
              </div>

              <p className="text-gray-600 dark:text-gray-300 text-center mb-6 min-h-[3rem]">
                {product.description}
              </p>

              <ul className="space-y-3 mb-8">
                {getProductFeatures(product.name).map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handlePurchase(product.priceId, product.mode)}
                disabled={loading === product.priceId || !navigator.onLine}
                className={`w-full ${
                  product.name.includes('Team')
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                    : product.name.includes('AI') && !product.name.includes('Basic')
                    ? 'bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700'
                    : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
                }`}
              >
                {loading === product.priceId ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('pricing.processing')}
                  </>
                ) : !navigator.onLine ? (
                  <>
                    <Wifi className="w-5 h-5 mr-2" />
                    {t('pricing.offline')}
                  </>
                ) : (
                  <>
                    {product.mode === 'subscription' ? t('pricing.subscribeNow') : t('pricing.buyNow')}
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">{t('pricing.needHelpChoosing')}</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
          {t('pricing.needHelpDesc')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            className="font-bold"
            onClick={() => window.location.href = 'mailto:dochelperagent@gmail.com'}
          >
            {t('pricing.contactSupport')}
          </Button>
          <Button
            onClick={() => navigate('/templates')}
            className="font-bold"
          >
            {t('pricing.tryFreeTemplates')}
          </Button>
        </div>
      </div>

      {/* Setup Instructions for Payment Issues */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
        <h2 className="text-2xl font-bold text-center mb-8">{t('pricing.paymentSystemSetup')}</h2>
        <div className="max-w-2xl mx-auto">
          <h3 className="font-bold mb-4 text-center">{t('pricing.forUsers')}</h3>
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="font-medium mb-1">{t('pricing.paymentIssues')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('pricing.paymentIssuesDesc')}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
              <h4 className="font-medium mb-1">{t('pricing.altPaymentMethods')}</h4>
              <p className="text-gray-600 dark:text-gray-300">
                {t('pricing.altPaymentMethodsDesc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8">
        <h2 className="text-2xl font-bold text-center mb-8">{t('pricing.faqTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-bold mb-2">{t('pricing.faqCancelQ')}</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {t('pricing.faqCancelA')}
            </p>
          </div>
          <div>
            <h3 className="font-bold mb-2">{t('pricing.faqRefundsQ')}</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {t('pricing.faqRefundsA')}
            </p>
          </div>
          <div>
            <h3 className="font-bold mb-2">{t('pricing.faqMethodsQ')}</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {t('pricing.faqMethodsA')}
            </p>
          </div>
          <div>
            <h3 className="font-bold mb-2">{t('pricing.faqUpgradeQ')}</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              {t('pricing.faqUpgradeA')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;